import { useMemo } from "react";
import useTranslator from "../../hooks/useTranslator";
import { Tooltip as ReactTooltip } from "react-tooltip";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import { getRentPercentage } from "../../helper/common";
import styles from "./TargetRent.module.css";

const TargetRent = ({
  monthlyIncome = 0,
  rent = 0,
  onRentChange,
  onAddGuarantor,
  onAddFlatmate,
  hasGuarantor = false,
  hasFlatmate = false,
  disabled = false,
  processing = false,
  isGuarantor = false,
}) => {
  const { _t } = useTranslator();
  const min = 10;
  const max = 50;

  const effortRate = useMemo(() => {
    if (!monthlyIncome || !rent) return min;
    const p = getRentPercentage(monthlyIncome, rent)?.exactValue;
    return Math.min(max, Math.max(min, Math.round(p) || min));
  }, [monthlyIncome, rent]);

  const comfortableMax = useMemo(
    () => Number(((monthlyIncome * 35) / 100).toFixed(2)),
    [monthlyIncome]
  );
  const tighterMax = useMemo(
    () => Number(((monthlyIncome * 45) / 100).toFixed(2)),
    [monthlyIncome]
  );
  const isPast = rent > comfortableMax;
  const fmt = (n) =>
    new Intl.NumberFormat("de-DE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);
  const handleSliderChange = (val) => {
    if (!monthlyIncome) {
      return;
    }
    if (disabled) return;
    onRentChange?.(Number(((monthlyIncome * val) / 100).toFixed(2)));
  };
  const maxRange = Math.max(tighterMax * 1.2, rent * 1.1, 1);
  const percent = Math.min(100, Math.max(0, (rent / maxRange) * 100));
  const disabledTooltip = processing
    ? _t(
        "profileProcessingWait",
        "Your profile is still being processed. Please wait until it's ready."
      )
    : disabled
      ? _t(
          "profileOutdatedRenewFirst",
          "Your profile is outdated. Please renew your profile first."
        )
      : undefined;

  return (
    <section
      className={`tenant-profile-placeholder-card tenant-profile-placeholder-card--target-rent ${styles.card}`}
      aria-labelledby="target-rent-title"
    >
      <h2 id="target-rent-title" className={styles.title}>
        {_t("yourRentalBarometer", "Your rental barometer")}
      </h2>
      <p className={styles.subtitle}>
        {isPast
          ? _t(
              "rentalBarometerPastComfortable",
              `At €${fmt(rent)}/month, you're past the comfortable range for your income.`,
              undefined,
              { rent: fmt(rent) }
            )
          : _t(
              "rentalBarometerComfortable",
              `At €${fmt(rent)}/month, you're within the comfortable range for your income.`,
              undefined,
              { rent: fmt(rent) }
            )}
      </p>
      <label className={styles.label}>
        {_t("enterRentConsidering", "Enter the rent you're considering")}
      </label>
      {disabled ? (
        <div
          style={{ display: "inline-block" }}
          data-tooltip-id="target-rent-input-tooltip"
        >
          <div className={styles.inputWrap} style={{ opacity: 0.5 }}>
            <span>€</span>
            <input
              type="number"
              value={rent}
              min={0}
              disabled={disabled}
              onChange={(e) => {
                if (disabled) return;
                onRentChange?.(Math.max(0, Number(e.target.value) || 0));
              }}
              aria-label={_t("targetRent", "Target Rent")}
              style={{ cursor: "not-allowed" }}
            />
            <span className={styles.suffix}>/month</span>
          </div>
          <ReactTooltip
            id="target-rent-input-tooltip"
            place="top"
            className="react-tooltip-styling"
            render={() => <p>{disabledTooltip}</p>}
          />
        </div>
      ) : (
        <div className={styles.inputWrap}>
          <span>€</span>
          <input
            type="number"
            value={rent}
            min={0}
            onChange={(e) =>
              onRentChange?.(Math.max(0, Number(e.target.value) || 0))
            }
            aria-label={_t("targetRent", "Target Rent")}
          />
          <span className={styles.suffix}>/month</span>
        </div>
      )}
      <div className={styles.meterWrap}>
        <span className={styles.valueLabel} style={{ left: `${percent}%` }}>
          €{fmt(rent)}
        </span>
        <div
          className={`skormeter ${styles.meter}`}
          style={{ "--w": ((effortRate - min) / (max - min)) * 100 }}
        >
          {disabled ? (
            <div
              style={{ display: "block" }}
              data-tooltip-id="target-rent-slider-tooltip"
            >
              <Slider
                min={min}
                max={max}
                value={effortRate}
                onChange={handleSliderChange}
                disabled
              />
              <ReactTooltip
                id="target-rent-slider-tooltip"
                place="top"
                className="react-tooltip-styling"
                render={() => <p>{disabledTooltip}</p>}
              />
            </div>
          ) : (
            <Slider
              min={min}
              max={max}
              value={effortRate}
              onChange={handleSliderChange}
              disabled={!monthlyIncome}
            />
          )}
          <div className="slider-labels">
            <span>{_t("comfortable", "Comfortable")}</span>
            <span></span>
            <span>{_t("moderate", "Moderate")}</span>
            <span></span>
            <span>{_t("elevated", "Elevated")}</span>
          </div>
        </div>
      </div>
      <div className={styles.scale}>
        <span>
          {_t("comfortableUpTo", "Comfortable · up to")} €{fmt(comfortableMax)}
        </span>
        <span>
          {_t("tighterAbove", "Tighter · above")} €{fmt(comfortableMax)}
          {tighterMax > comfortableMax ? ` · up to €${fmt(tighterMax)}` : ""}
        </span>
      </div>

      {!isGuarantor && isPast && !(hasGuarantor && hasFlatmate) && (
        <div className={styles.callout}>
          <p>
            {_t(
              "rentalBarometerCallout",
              "A guarantor or flatmate can help offset this and keep your application strong."
            )}
          </p>
          <div className={styles.actions}>
            {!hasGuarantor &&
              (disabled ? (
                <div
                  style={{ display: "inline-block" }}
                  data-tooltip-id="target-rent-guarantor-tooltip"
                >
                  <button
                    type="button"
                    className={styles.primary}
                    onClick={onAddGuarantor}
                    disabled={disabled}
                    style={{ opacity: 0.5, cursor: "not-allowed" }}
                  >
                    {_t("addGuarantor", "Add guarantor")}
                  </button>
                  <ReactTooltip
                    id="target-rent-guarantor-tooltip"
                    place="top"
                    className="react-tooltip-styling"
                    render={() => <p>{disabledTooltip}</p>}
                  />
                </div>
              ) : (
                <button
                  type="button"
                  className={styles.primary}
                  onClick={onAddGuarantor}
                >
                  {_t("addGuarantor", "Add guarantor")}
                </button>
              ))}
            {!hasFlatmate &&
              (disabled ? (
                <div
                  style={{ display: "inline-block" }}
                  data-tooltip-id="target-rent-flatmate-tooltip"
                >
                  <button
                    type="button"
                    className={styles.secondary}
                    onClick={onAddFlatmate}
                    disabled={disabled}
                    style={{ opacity: 0.5, cursor: "not-allowed" }}
                  >
                    {_t("addFlatmate", "Add flatmate")}
                  </button>
                  <ReactTooltip
                    id="target-rent-flatmate-tooltip"
                    place="top"
                    className="react-tooltip-styling"
                    render={() => <p>{disabledTooltip}</p>}
                  />
                </div>
              ) : (
                <button
                  type="button"
                  className={styles.secondary}
                  onClick={onAddFlatmate}
                >
                  {_t("addFlatmate", "Add flatmate")}
                </button>
              ))}
          </div>
        </div>
      )}
    </section>
  );
};

export default TargetRent;
